import { useEffect, useRef, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
	readonly platforms: string[];
	readonly userChoice: Promise<{
		outcome: 'accepted' | 'dismissed';
		platform: string;
	}>;
	prompt(): Promise<void>;
}

const checkForStandaloneDisplayMode = () =>
	('standalone' in navigator && (navigator as Navigator & { standalone: boolean }).standalone) ||
	window.matchMedia('(display-mode: standalone)').matches ||
	document.referrer.includes('android-app://');

const getOSFromUserAgent = () => {
	// 2nd part for iPad on iOS 13 specifically
	// MSStream used to exclude IE11
	if (
		(/iPad|iPhone|iPod/.test(navigator.userAgent) ||
			(navigator.userAgent.includes('Mac') && 'ontouchend' in document)) &&
		!('MSStream' in window && window.MSStream)
	) {
		return 'iOS';
	}

	if (/Android/.test(navigator.userAgent)) {
		return 'Android';
	}

	return undefined;
};

const usePWA = () => {
	const [isAndroid, setIsAndroid] = useState(false);
	const [isIOS, setIsIOS] = useState(false);
	const [isStandalone, setIsStandalone] = useState(false);
	const [isInstalled, setIsInstalled] = useState(false);

	const isMobile = isAndroid || isIOS;

	const deferredInstallPrompt = useRef<BeforeInstallPromptEvent>();

	const isInstallable = !!deferredInstallPrompt.current || (isIOS && !isStandalone);

	const installPWA = () => {
		deferredInstallPrompt && deferredInstallPrompt.current?.prompt();

		(async () => {
			const { outcome } = (await deferredInstallPrompt.current?.userChoice) ?? {};

			if (outcome === 'accepted') {
				setIsInstalled(true);
			}
		})().catch((error) => {
			throw error;
		});
	};

	const interceptInstallPrompt = () => {
		window.addEventListener('beforeinstallprompt', (event) => {
			event.preventDefault();

			deferredInstallPrompt.current = event as BeforeInstallPromptEvent;
		});
	};

	const initializeState = () => {
		const isStandaloneMode = checkForStandaloneDisplayMode();

		setIsStandalone(isStandaloneMode);

		const os = getOSFromUserAgent();

		if (os === 'iOS') {
			setIsIOS(true);
		} else if (os === 'Android') {
			setIsAndroid(true);
		}

		if (isStandaloneMode) {
			setIsInstalled(true);
		}
	};

	const detectInstalledRelatedApps = async () => {
		if ('getInstalledRelatedApps' in navigator) {
			const relatedApps = await (navigator.getInstalledRelatedApps as () => Promise<[]>)();

			setIsInstalled((prev) => (!prev ? relatedApps.length > 0 : prev));
		}
	};

	useEffect(() => {
		interceptInstallPrompt();
		initializeState();
		(async () => detectInstalledRelatedApps())().catch((error) => {
			throw error;
		});
	}, []);

	return {
		isAndroid,
		isIOS,
		isMobile,
		isStandalone,
		installPWA,
		isInstalled,
		isInstallable,
	};
};

export default usePWA;
